import { oc } from "@orpc/contract";
import { z } from "zod";
import { GetUserProfileQuerySchema, GetUserProfileParamSchema, UpdateUserProfileInputSchema, UserProfileResponseSchema, QuickStatsResponseSchema, TeamMemberInfoResponseSchema, CompensationResponseSchema, AvailabilityResponseSchema, UpdateAvailabilityInputSchema, } from "@hourly/api/userProfile";
import { IncentivesResponseSchema } from "@hourly/api/incentive";
/**
 * Get current user's profile (self-access, userId from JWT)
 */
export const getUserProfile = oc
    .route({
    method: "GET",
    path: "/user-profile",
    summary: "Get current user's profile",
    description: "Retrieve the authenticated user's profile information for a specific company",
    tags: ["User Profile"],
})
    .input(GetUserProfileQuerySchema)
    .output(UserProfileResponseSchema);
/**
 * Get current user's quick stats (self-access)
 */
export const getQuickStats = oc
    .route({
    method: "GET",
    path: "/user-profile/quick-stats",
    summary: "Get current user's quick stats",
    description: "Retrieve quick statistics for the authenticated user",
    tags: ["User Profile"],
})
    .input(z.object({}))
    .output(QuickStatsResponseSchema);
/**
 * Update current user's profile (self-access)
 */
export const updateUserProfile = oc
    .route({
    method: "PUT",
    path: "/user-profile",
    summary: "Update current user's profile",
    description: "Update the authenticated user's profile information",
    tags: ["User Profile"],
})
    .input(UpdateUserProfileInputSchema)
    .output(UserProfileResponseSchema);
/**
 * Get another user's profile by ID (manager view, requires permission)
 */
export const getUserProfileById = oc
    .route({
    method: "GET",
    path: "/user-profile/{userId}",
    summary: "Get user profile by ID",
    description: "Retrieve a specific user's profile information (requires manager permission)",
    tags: ["User Profile"],
})
    .input(GetUserProfileParamSchema.merge(GetUserProfileQuerySchema))
    .output(UserProfileResponseSchema);
/**
 * Get team member info for a user (manager view, requires permission)
 */
export const getTeamMemberInfo = oc
    .route({
    method: "GET",
    path: "/user-profile/{userId}/team-member-info",
    summary: "Get team member info",
    description: "Retrieve team member information for a specific user (requires manager permission)",
    tags: ["User Profile"],
})
    .input(GetUserProfileParamSchema.merge(GetUserProfileQuerySchema))
    .output(TeamMemberInfoResponseSchema);
/**
 * Get compensation info for a user (manager view, requires permission)
 */
export const getCompensation = oc
    .route({
    method: "GET",
    path: "/user-profile/{userId}/compensation",
    summary: "Get user compensation",
    description: "Retrieve compensation information for a specific user (requires manager permission)",
    tags: ["User Profile"],
})
    .input(GetUserProfileParamSchema.merge(GetUserProfileQuerySchema))
    .output(CompensationResponseSchema);
/**
 * Get availability info for a user (manager view, requires permission)
 */
export const getAvailability = oc
    .route({
    method: "GET",
    path: "/user-profile/{userId}/availability",
    summary: "Get user availability",
    description: "Retrieve availability information for a specific user (requires manager permission)",
    tags: ["User Profile"],
})
    .input(GetUserProfileParamSchema.merge(GetUserProfileQuerySchema))
    .output(AvailabilityResponseSchema);
/**
 * Get incentives info for a user (manager view, requires permission)
 */
export const getIncentives = oc
    .route({
    method: "GET",
    path: "/user-profile/{userId}/incentives",
    summary: "Get user incentives",
    description: "Retrieve incentive information for a specific user (requires manager permission)",
    tags: ["User Profile"],
})
    .input(GetUserProfileParamSchema.merge(GetUserProfileQuerySchema))
    .output(IncentivesResponseSchema);
/**
 * Get current user's availability (self-access, userId from JWT)
 */
export const getSelfAvailability = oc
    .route({
    method: "GET",
    path: "/user-profile/availability",
    summary: "Get current user's availability",
    description: "Retrieve availability information for the authenticated user",
    tags: ["User Profile"],
})
    .input(z.object({}))
    .output(AvailabilityResponseSchema);
/**
 * Update current user's availability (self-access)
 */
export const updateAvailability = oc
    .route({
    method: "PUT",
    path: "/user-profile/availability",
    summary: "Update current user's availability",
    description: "Update availability schedules for the authenticated user (replace-all strategy)",
    tags: ["User Profile"],
})
    .input(UpdateAvailabilityInputSchema)
    .output(AvailabilityResponseSchema);
/**
 * User Profile contract - all endpoints for user profile management
 */
export const userProfileContract = {
    get: getUserProfile,
    getById: getUserProfileById,
    update: updateUserProfile,
    quickStats: getQuickStats,
    teamMemberInfo: getTeamMemberInfo,
    compensation: getCompensation,
    availability: getAvailability,
    selfAvailability: getSelfAvailability,
    updateAvailability: updateAvailability,
    incentives: getIncentives,
};
//# sourceMappingURL=userProfile.js.map